import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Newspaper, Loader2, ArrowRight, Calendar, Images } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/FooterCTA";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function fmtDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return `${dt.getDate()} de ${MONTHS[dt.getMonth()]}. ${dt.getFullYear()}`;
}

export default function NoticiasPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts")
      .then(({ data }) => setPosts(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-[#eef1f7] flex flex-col">
      <Navbar />
      <div className="pt-16 sm:pt-20">
        <Breadcrumbs items={[{ label: "Noticias" }]} />
      </div>
      <header className="bg-navy text-white pt-10 pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <span className="inline-flex items-center gap-2 text-gold text-xs font-bold tracking-[0.18em] uppercase">
            <Newspaper className="h-4 w-4" /> Noticias del club
          </span>
          <h1 data-testid="noticias-title" className="mt-3 text-4xl sm:text-6xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Lo último del <span className="text-gold">Comandante</span>
          </h1>
          <p className="mt-3 text-white/80 max-w-2xl">Novedades, partidos, eventos y toda la vida del club en un solo lugar.</p>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-10">
        {loading ? (
          <div className="py-24 flex justify-center text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="py-24 text-center text-gray-400">
            <Newspaper className="h-10 w-10 mx-auto" />
            <p className="mt-3">Todavía no hay noticias publicadas.</p>
          </div>
        ) : (
          <>
            {featured && (
              <Link to={`/noticias/${featured.slug}`} data-testid="noticia-featured" className="group block rounded-3xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-shadow mb-8">
                <div className="grid md:grid-cols-2">
                  <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[320px] bg-navy/5 overflow-hidden">
                    {featured.cover_url
                      ? <img src={featured.cover_url} alt={featured.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      : <div className="w-full h-full flex items-center justify-center text-navy/20"><Newspaper className="h-16 w-16" /></div>}
                    <span className="absolute top-4 left-4 bg-gold text-navy text-xs font-bold px-3 py-1 rounded-full">Destacada</span>
                  </div>
                  <div className="p-6 sm:p-9 flex flex-col justify-center">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Calendar className="h-3.5 w-3.5" /> {fmtDate(featured.date || featured.created_at)}
                      {featured.album_slug && <span className="inline-flex items-center gap-1 text-navy/60"><Images className="h-3.5 w-3.5" /> con álbum</span>}
                    </div>
                    <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-navy tracking-tight" style={{ fontFamily: "Outfit" }}>{featured.title}</h2>
                    {featured.excerpt && <p className="mt-3 text-gray-500 leading-relaxed line-clamp-3">{featured.excerpt}</p>}
                    <span className="mt-5 inline-flex items-center gap-2 text-navy font-bold text-sm">Leer noticia <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </div>
              </Link>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/noticias/${p.slug}`} data-testid={`noticia-card-${p.slug}`} className="group block rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow h-full">
                    <div className="relative aspect-[16/10] bg-navy/5 overflow-hidden">
                      {p.cover_url
                        ? <img src={p.cover_url} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        : <div className="w-full h-full flex items-center justify-center text-navy/20"><Newspaper className="h-12 w-12" /></div>}
                      {p.album_slug && <span className="absolute top-3 right-3 bg-white/90 text-navy text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1"><Images className="h-3 w-3" /> Álbum</span>}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-1.5 text-xs text-gray-400"><Calendar className="h-3.5 w-3.5" /> {fmtDate(p.date || p.created_at)}</div>
                      <h3 className="mt-2 font-bold text-navy text-lg leading-snug line-clamp-2" style={{ fontFamily: "Outfit" }}>{p.title}</h3>
                      {p.excerpt && <p className="mt-2 text-sm text-gray-500 line-clamp-2">{p.excerpt}</p>}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
